import React from 'react';
import Skeleton from '../core/Skeleton';

/**
 * AppLoadingSkeleton - Full-page loading skeleton for initial app load
 *
 * Displayed while i18n translations are loading to prevent
 * the flash of translation keys (e.g., "common:actions.save").
 *
 * Shows a generic app layout skeleton that works for both
 * authenticated and public pages.
 */
const AppLoadingSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50" role="status" aria-label="Loading application">
      {/* Top Navigation Bar Skeleton */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo placeholder */}
            <div className="flex items-center space-x-3">
              <Skeleton variant="rectangular" width="32px" height="32px" className="rounded-md" />
              <Skeleton variant="text" width="100px" height="20px" />
            </div>

            {/* Navigation items placeholder */}
            <div className="hidden md:flex items-center space-x-6">
              <Skeleton variant="text" width="60px" height="16px" />
              <Skeleton variant="text" width="70px" height="16px" />
              <Skeleton variant="text" width="80px" height="16px" />
            </div>

            {/* User avatar placeholder */}
            <div className="flex items-center space-x-3">
              <Skeleton variant="circular" width="36px" height="36px" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Skeleton */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page title */}
        <div className="mb-8">
          <Skeleton variant="text" width="200px" height="28px" className="mb-2" />
          <Skeleton variant="text" width="300px" height="16px" />
        </div>

        {/* Content cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
              <div className="space-y-4">
                <Skeleton variant="text" width="70%" height="20px" />
                <Skeleton variant="text" width="90%" height="14px" />
                <Skeleton variant="text" width="50%" height="14px" />
                <div className="pt-4">
                  <Skeleton variant="rectangular" width="100%" height="36px" className="rounded-md" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AppLoadingSkeleton;
